import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
    const [form, setForm] = useState({ email: "", password: "" });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [params] = useSearchParams();

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const res = await login(form.email, form.password);
        setLoading(false);
        if (!res.ok) {
            if (res.needsVerification) {
                nav("/verify-otp", { state: { email: res.email, message: res.error } });
                return;
            }
            setErr(res.error);
            return;
        }
        const next = params.get("next");
        nav(next || loc.state?.from || "/dashboard");
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-navy-900 grid-pattern px-6 py-12">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 justify-center mb-8">
                    <div className="w-28 h-28 flex items-center justify-center">
                        <img
                            src="/gpm-logo.png"
                            alt="GPM"
                            className="w-full h-full object-contain drop-shadow-[0_0_24px_rgba(218,165,32,0.5)]"
                        />
                    </div>
                    <div className="text-left">
                        <div className="font-heading font-bold text-white text-xl leading-tight">Global Property Mart</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-gold-500/90">One Platform · Every Need</div>
                    </div>
                </Link>

                <div className="rounded-3xl glass p-8 gold-border-glow">
                    <h1 className="font-heading text-3xl font-bold text-white">Welcome back</h1>
                    <p className="text-white/60 text-sm mt-1">Sign in to manage your properties.</p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
                        <div>
                            <label className="text-xs text-white/60 mb-1 block">Email</label>
                            <input
                                required type="email"
                                data-testid="login-email"
                                value={form.email}
                                onChange={(e) => setForm({...form, email:e.target.value})}
                                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white"
                                placeholder="you@example.com"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-white/60 mb-1 block">Password</label>
                            <input
                                required type="password"
                                data-testid="login-password"
                                value={form.password}
                                onChange={(e) => setForm({...form, password:e.target.value})}
                                className="w-full bg-navy-900 border border-white/10 rounded-md px-4 py-3 text-sm text-white"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
                        <button
                            type="submit" disabled={loading}
                            data-testid="login-submit"
                            className="w-full px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold hover:bg-gold-100 disabled:opacity-60"
                        >{loading ? "Signing in…" : "Sign in"}</button>
                        <div className="text-right">
                            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-gold-500 hover:text-gold-100">Forgot password?</Link>
                        </div>
                    </form>

                    <div className="mt-6 text-center text-sm text-white/60">
                        Don't have an account?{" "}
                        <Link to={`/register${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="text-gold-500 font-medium hover:text-gold-100">Create one</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
